import { clampChroma, converter, formatHex } from 'culori';

const toOklch = converter('oklch');

/**
 * Below this OKLCH chroma a color reads as gray: its hue is perceptually
 * meaningless, so rotating it produces muddy near-duplicates rather than real
 * harmonies. We treat such colors as achromatic and skip rotation. The mock in
 * the plan keyed only off `h == null`, but near-gray colors still carry a hue —
 * the chroma floor is what actually catches them.
 */
export const ACHROMATIC_CHROMA = 0.02;

/** True when a color has no usable hue (gray/near-gray); harmonies collapse. */
export function isAchromatic(hex: string): boolean {
  const c = toOklch(hex);
  return c == null || c.h == null || (c.c ?? 0) < ACHROMATIC_CHROMA;
}

/**
 * Rotate a color's hue by `deg` around the OKLCH wheel, preserving lightness
 * and chroma. Achromatic colors are returned unchanged (no invented hue).
 */
export function rotateHue(hex: string, deg: number): string {
  const c = toOklch(hex);
  if (c == null || c.h == null || (c.c ?? 0) < ACHROMATIC_CHROMA) return hex;
  const rotated = { ...c, h: (((c.h + deg) % 360) + 360) % 360 };
  // The rotated color may fall outside sRGB; reduce chroma to fit while keeping
  // L and H. Without this, formatHex clips channels and skews the hue (a
  // saturated complement could drift ~12° off), breaking the wheel relationship.
  return formatHex(clampChroma(rotated, 'oklch'));
}

export const complementary = (base: string): string[] => [base, rotateHue(base, 180)];
export const analogous = (base: string): string[] => [rotateHue(base, -30), base, rotateHue(base, 30)];
export const triadic = (base: string): string[] => [base, rotateHue(base, 120), rotateHue(base, 240)];

export type HarmonyKind = 'complementary' | 'analogous' | 'triadic';

export interface Harmony {
  kind: HarmonyKind;
  label: string;
  colors: string[];
}

/** All three harmony sets derived from a base color, in display order. */
export function harmonies(base: string): Harmony[] {
  return [
    { kind: 'complementary', label: 'Complementary', colors: complementary(base) },
    { kind: 'analogous', label: 'Analogous', colors: analogous(base) },
    { kind: 'triadic', label: 'Triadic', colors: triadic(base) },
  ];
}
